import React, { useState } from "react";
import { SwissDateField } from "@/components/SwissDateInput";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// Iter158: 0.00-CHF-Warnung vor Buchung
export function ZeroAmountDialog({ receipt, onClose, onReanalyze, onForceBook }) {
  return (
    <Dialog open={!!receipt} onOpenChange={(open) => !open && onClose()}>
      <DialogContent data-testid="receipt-zero-amount-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-900">
            ⚠️ Betrag ist 0.00 CHF
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm text-slate-700">
          <p>
            Dieser Beleg hat aktuell keinen Betrag oder <b>0.00 CHF</b>.
            Möchtest du ihn trotzdem verbuchen?
          </p>
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
            💡 Tipp: Klicke stattdessen auf <b>&quot;Neu analysieren&quot;</b> — die
            Vision-KI kann den Betrag oft direkt aus dem Foto erkennen.
          </div>
        </div>
        <div className="flex flex-wrap justify-end gap-2 pt-3">
          <Button variant="ghost" onClick={onClose} data-testid="receipt-zero-cancel">
            Abbrechen
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              const id = receipt?._id;
              onClose();
              if (id) onReanalyze(id);
            }}
            data-testid="receipt-zero-reanalyze"
          >
            <RefreshCw className="h-3.5 w-3.5 mr-1" />
            Neu analysieren
          </Button>
          <Button
            onClick={() => {
              const id = receipt?._id;
              onClose();
              if (id) onForceBook(id);
            }}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="receipt-zero-force-book"
          >
            Trotzdem verbuchen
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Iter159: Datum-fehlt-Warndialog vor Buchung
export function MissingDateDialog({ receipt, onClose, onReanalyze, onSavedAndBook }) {
  const [dateInput, setDateInput] = useState("");

  const close = () => {
    setDateInput("");
    onClose();
  };

  const saveAndBook = async () => {
    const id = receipt?._id;
    if (!id || !dateInput) {
      toast.error("Bitte Datum wählen");
      return;
    }
    try {
      await apiClient.put(`/receipts/${id}`, { date: dateInput });
      toast.success("Datum gespeichert. Buche jetzt…");
      close();
      onSavedAndBook(id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    }
  };

  return (
    <Dialog open={!!receipt} onOpenChange={(open) => !open && close()}>
      <DialogContent data-testid="receipt-missing-date-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-900">
            📅 Datum fehlt
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm text-slate-700">
          <p>
            Der Beleg hat <b>kein Datum</b>. Ein Beleg ohne Datum kann nicht
            korrekt verbucht werden (MWST-Periode!).
          </p>
          <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-800">
            💡 Tipp: Das Belegdatum ist das <b>Ausstellungsdatum</b> auf dem
            Beleg (nicht heute).
          </div>
          <div>
            <label className="text-xs font-medium text-slate-700 block mb-1">
              Belegdatum eingeben (JJJJ-MM-TT)
            </label>
            <SwissDateField
              
              value={dateInput}
              onChange={(e) => setDateInput(e.target.value)}
              data-testid="receipt-missing-date-input"
            />
          </div>
        </div>
        <div className="flex flex-wrap justify-end gap-2 pt-3">
          <Button variant="ghost" onClick={close} data-testid="receipt-missing-date-cancel">
            Abbrechen
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              const id = receipt?._id;
              close();
              if (id) onReanalyze(id);
            }}
            data-testid="receipt-missing-date-reanalyze"
          >
            <RefreshCw className="h-3.5 w-3.5 mr-1" />
            Neu analysieren
          </Button>
          <Button
            onClick={saveAndBook}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="receipt-missing-date-save-and-book"
            disabled={!dateInput}
          >
            Datum speichern & buchen
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
